import { H2 } from '@berlitz/globals'
import Input from '@berlitz/input'
import Spacer from '@berlitz/spacer'
import SearchAlt from '@berlitz/streamline-icons/lib/bold/01-Interface-Essential/25-Search/SearchAlternate'
import MultiSelect from '@berlitzplatforms/micro.ui.multi-select-legacy'
import PortalPrograms from '@classes/Programs'
import HR from '@components/HR'
import RoleBasedView from '@components/RoleBasedView'
import Select from '@components/Select'
import { ISearchInstructorFilters } from '@interfaces/ISearchInstructorFilters'
import { StoreProps } from '@interfaces/StoreState'
import { SelectValue } from 'antd/lib/select'
import { uniq } from 'lodash'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Skeleton from 'react-loading-skeleton'
import { useSelector } from 'react-redux'
import FilterModal from './FilterModal'
import { FILTERS } from './filters'
import FindALesson from './FindALesson'
import {
  DesktopFilters,
  DesktopView,
  FieldLabel,
  Filter,
  Flex,
  LeftContent,
  MFilterWrapper,
  MInputWrapper,
  MISearchBtn,
  MobileFilterBtn,
  MobileSearchBtn,
  MobileView,
  RightContent,
  SearchBar,
  SearchFilter,
  SearchIcon,
} from './style'

export type TUpdateFilters = (type: string, value: string[] | string | undefined) => void

interface PageFiltersProps {
  bulkUpdateFilters: (_f: ISearchInstructorFilters) => void
  updateFilters: TUpdateFilters
  loading?: boolean
  programLang?: string
  handleInputChange?: (evt: React.ChangeEvent<HTMLInputElement>) => void
  handleDDChange?: (e: SelectValue) => void // to be used when using auto-complete (not implemented yet)
  inputValue?: string
  filters?: ISearchInstructorFilters
  readOnly?: boolean
  programId: string
}

const noop = () => {}

const Header = ({ loading }) => (
  <H2>
    {loading ? (
      <Skeleton width="190px" height="21px" />
    ) : (
      <FormattedMessage id="Find an instructor" defaultMessage="Find an instructor" />
    )}
  </H2>
)

const PageFilters: React.FC<PageFiltersProps> = ({
  bulkUpdateFilters,
  updateFilters,
  loading,
  inputValue = '',
  programLang = 'English',
  handleInputChange = noop,
  filters = {},
  readOnly,
  programId,
}) => {
  const intl = useIntl()
  const [showMInput, setShowMInput] = useState(false)
  const [showMFilters, setShowMFilters] = useState(false)
  const { StudentProgram } = useSelector(({ userProfile }: StoreProps) => ({
    StudentProgram: new PortalPrograms(
      userProfile?.info?.StudentProfile?.StudentProfileInfo?.ProgramStudentWrappers ?? []
    ).getProgram(programId),
  }))
  const hasSingleSearch = StudentProgram?.program?.SingleSessionScheduling

  useEffect(() => {
    if (showMInput) {
      document.getElementById('mobile-input')?.focus()
    }
  }, [showMInput])

  useEffect(() => {
    if (loading) {
      setShowMInput(false)
    }
  }, [loading])

  return (
    <>
      <DesktopView>
        <Flex>
          <LeftContent>
            <Header loading={loading} />
            <DesktopFilters>
              <Filter>
                {loading ? (
                  <div>
                    <FieldLabel>
                      <Skeleton height="15px" width="120px" />
                    </FieldLabel>
                    <Skeleton height="36px" width="157px" />
                  </div>
                ) : (
                  <Select
                    label={intl.formatMessage({ id: 'Speaks', defaultMessage: 'Speaks' })}
                    options={[{ label: programLang }]}
                    value={programLang}
                    onChange={() => {}}
                    disabled
                  />
                )}
              </Filter>
              <Filter>
                <FieldLabel>
                  {loading ? (
                    <Skeleton height="15px" width="120px" />
                  ) : (
                    <FormattedMessage id="Also speaks" defaultMessage="Also speaks" />
                  )}
                </FieldLabel>
                {loading ? (
                  <Skeleton height="36px" width="157px" />
                ) : (
                  <MultiSelect
                    options={FILTERS.LANGUAGES}
                    onChange={(value) => updateFilters('speaks', value)}
                    preSelect={uniq(filters.speaks || [])}
                  />
                )}
              </Filter>
              <RoleBasedView configId="c::scheduling::instructors::specialisation">
                <Filter>
                  <FieldLabel>
                    {loading ? (
                      <Skeleton height="15px" width="120px" />
                    ) : (
                      <FormattedMessage id="Specialisation" defaultMessage="Specialisation" />
                    )}
                  </FieldLabel>
                  {loading ? (
                    <Skeleton height="36px" width="157px" />
                  ) : (
                    <MultiSelect
                      options={FILTERS.SPECIALIZATIONS}
                      preSelect={uniq(filters.specialisation || [])}
                      onChange={(value) => updateFilters('specialisation', value)}
                    />
                  )}
                </Filter>
              </RoleBasedView>
              <SearchFilter readOnly={readOnly || !hasSingleSearch}>
                <Spacer size="sm" />
                <Spacer size="xxxs" />
                <Spacer size="xs" />
                <SearchBar>
                  {loading ? (
                    <Skeleton height="36px" width="256px" />
                  ) : (
                    <>
                      <Input
                        type="text"
                        name="filterName"
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder={intl.formatMessage({
                          id: 'Search for an instructor',
                          defaultMessage: 'Search for an instructor',
                        })}
                      />
                      <SearchIcon />
                    </>
                  )}
                </SearchBar>
              </SearchFilter>
            </DesktopFilters>
          </LeftContent>
          {hasSingleSearch && (
            <RightContent>
              {!readOnly ? (
                loading ? (
                  <Skeleton width="190px" height="98px" />
                ) : (
                  <FindALesson programId={programId} hasSingleSearch={hasSingleSearch} />
                )
              ) : null}
            </RightContent>
          )}
        </Flex>
        <Spacer size="xxs" />
        <HR />
        <Spacer size="xxs" />
      </DesktopView>
      <MobileView>
        <Header loading={loading} />
        <MInputWrapper show={showMInput}>
          <Input
            id={'mobile-input'}
            type="text"
            name="filterName"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={intl.formatMessage({
              id: 'Search for an instructor',
              defaultMessage: 'Search for an instructor',
            })}
          />
          <MISearchBtn show onClick={() => setShowMInput(false)}>
            <SearchAlt />
          </MISearchBtn>
        </MInputWrapper>
        <MFilterWrapper>
          {loading ? (
            <>
              <div style={{ width: '100%' }}>
                <Skeleton width="100%" height="34px" />
              </div>
              <Skeleton width="40px" height="34px" />
            </>
          ) : (
            <>
              <MobileFilterBtn onClick={() => setShowMFilters(true)}>
                <FormattedMessage id="Modify filters" defaultMessage="Modify filters" />
              </MobileFilterBtn>
              <MobileSearchBtn
                show={!showMInput}
                outline
                onClick={() => {
                  setShowMInput(true)
                }}
              >
                <SearchAlt />
              </MobileSearchBtn>
            </>
          )}
        </MFilterWrapper>
        <Spacer size="md" />
        {hasSingleSearch && (
          <>
            {!readOnly ? (
              loading ? (
                <Skeleton width="100%" height="34px" />
              ) : (
                !readOnly && <FindALesson programId={programId} hasSingleSearch={hasSingleSearch} />
              )
            ) : null}
            {!readOnly && <Spacer size="md" />}
          </>
        )}
      </MobileView>
      <FilterModal
        open={showMFilters}
        onClose={() => setShowMFilters(false)}
        updateFilters={bulkUpdateFilters}
        filters={filters}
        programLang={programLang}
      />
    </>
  )
}

export default PageFilters
